/**
 * Bookmark Service
 * Handles all bookmark-related API calls
 *
 * @module services/BookmarkService
 */

import BaseService from "core/api/BaseService";
import { API_ENDPOINTS } from "utils/constants";

class BookmarkService extends BaseService {
  constructor() {
    super(API_ENDPOINTS.BOOKMARKS.BASE);
  }

  /**
   * Get all user bookmarks
   * @param {Object} params - Query parameters
   * @returns {Promise} Bookmarks list
   */
  async getMyBookmarks(params = {}) {
    return this.getAll(params);
  }

  /**
   * Check if item is bookmarked
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @returns {Promise} Bookmark status
   */
  async checkBookmark(itemType, itemId) {
    return this.get(API_ENDPOINTS.BOOKMARKS.CHECK(itemType, itemId));
  }

  /**
   * Add bookmark
   * @param {Object} bookmarkData - Bookmark data
   * @param {string} bookmarkData.itemType - Type of item ('course' or 'tutorial')
   * @param {string} bookmarkData.itemId - Item ID
   * @returns {Promise} Created bookmark
   */
  async addBookmark(bookmarkData) {
    return this.create(bookmarkData);
  }

  /**
   * Remove bookmark
   * @param {string} bookmarkId - Bookmark ID
   * @returns {Promise} Delete result
   */
  async removeBookmark(bookmarkId) {
    return this.delete(bookmarkId);
  }

  /**
   * Remove bookmark by item
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @returns {Promise} Delete result
   */
  async removeByItem(itemType, itemId) {
    return this.deleteCustom(
      API_ENDPOINTS.BOOKMARKS.DELETE_BY_ITEM(itemType, itemId)
    );
  }

  /**
   * Toggle bookmark (add if not exists, remove if exists)
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @param {string} itemId - Item ID
   * @returns {Promise} Toggle result
   */
  async toggleBookmark(itemType, itemId) {
    const checkResult = await this.checkBookmark(itemType, itemId);

    if (checkResult.success && checkResult.data?.isBookmarked) {
      return this.removeByItem(itemType, itemId);
    } else {
      return this.addBookmark({ itemType, itemId });
    }
  }

  /**
   * Get bookmarks by type
   * @param {string} itemType - Type of item ('course' or 'tutorial')
   * @returns {Promise} Filtered bookmarks
   */
  async getByType(itemType) {
    return this.getAll({ itemType });
  }
}

// Export singleton instance
export default new BookmarkService();

